/// <reference path="../typedefs/dash.js" />

import strings from 'utils/strings'
import { createSelector } from 'reselect'
import {
  get,
  getEq,
  filter,
  safeToJS,
  getOr,
  getInOr,
  pick,
  getIn,
  omit,
  push,
  pickToJS,
  pickToArray,
  has,
  notEmpty,
  values as immuValues,
} from 'utils/immutable'
import { Map, List, Set } from 'immutable'
import {
  compose,
  pathOr,
  includes,
  propEq,
  T,
  cond,
  always,
  propOr,
  ifElse,
  equals,
  path,
  values,
  prop,
} from 'ramda'
import {
  MSF,
  MST,
  POCKET,
  OHSSP,
  UNKNOWN_DETAIL,
  SWITCH_DETAIL_BY_DASH_TYPE,
  MSF_DETAIL_BY_DASH_TYPE,
  MSF_CAVITY_PLUG_PART,
  UNASSIGNED_PANEL_PART,
  AU_PANEL_LOCATION_PREFIX,
  ICU,
  OVLHP,
  OVRHP,
  OVCPL,
  excludedLocationTypes,
  DIGITAL_DASH_CODE,
  DIGITAL_DASH_LOCATION_ID,
  AUX_PANEL_LOCATION_ID,
  BREAK_LOCATIONS_42N,
  AGPNL,
  DASH_TYPE_GROUPS,
  OVERHEAD_DAY_CAB_LOCATION_ID_PREFIX,
  OVERHEAD_EXTENDED_CAB_LOCATION_ID_PREFIX,
  SLEEPER_DASH_CODES,
  MSF_POCKET_LOCATIONS_MAP,
  vehicleTypesForAddMsf,
  OVERHEAD_PANELS,
  POC,
  RHD_43N_38N_CODE,
  DETAIL,
  STAGED_PANEL_PART,
  SLEEPER_LOCATION_PREFIX,
  LOCATION_TYPE_SINGLE_SWITCH_MSF,
  LOCATION_TYPE_TBB_SWITCH,
  TEM_PANEL_LOCATION_ID,
  SWITCH_DETAIL_BY_MODEL_CODE,
  CEEA_DASHBOARD_TYPES,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import {
  EDIT_PLUS_S_AND_G,
  EDIT_PLUS_PRE_PROD_S_AND_G,
} from 'constants/authorization'
import {
  VEHICLE_TYPE_CHEC_PLUS,
  linkedLogicListKeysByTruckType,
  REMOVED,
  TRUCK_SUBTYPE_42N,
  VEHICLE_TYPE_M2,
  TRUCK_SUBTYPE_38N,
} from 'utils/constants'
import { HARDWIRED_COMPONENT, HARDWIRED_SWITCH } from 'constants/dash'

import LocationShape from 'components/SwitchAndGauge/Dash/LocationShape'
import { DASH_REDUX_KEY } from 'reducers/dash'
import {
  isUnassignedComponent,
  isAssignedComponent,
  makeFillFileName,
  makeBlankFileName,
  isStagedComponent,
  getPartGraphicByFilename,
  createPartFileName,
  get42NPlugDisplayNumber,
} from 'components/SwitchAndGauge/utils'
import { isArray, insertIf } from 'utils/types'

const DASH_META_KEYS = ['name', 'panels', 'width', 'height', 'panelsStyle']

const dashSelector = (state) => getOr(Map(), DASH_REDUX_KEY, state)

const blockedListSelector = createSelector(
  dashSelector,
  getOr(List(), 'blockedList')
)

const blackListSelector = createSelector(
  dashSelector,
  getOr(List(), 'blackList')
)

export const getBlockedList = createSelector(blockedListSelector, safeToJS)

const vehicleSelector = (state, { serialNumber }) =>
  state.getIn(['truck', 'vehicles', serialNumber])

const vehicleDashType = createSelector(vehicleSelector, (vehicle) => {
  if (!vehicle) {
    return Map()
  }
  return pick(
    ['dashboardType', 'dashboardDbCodes', 'serialNumber', 'subType'],
    vehicle
  )
})

export const getIsPreProduction = (state) => {
  const serialNumber = getIn(['dash', 'serialNumber'], state)
  const isPreProduction = getIn(
    ['truck', 'vehicles', serialNumber, 'isPreProduction'],
    state
  )
  return isPreProduction
}

export const getIsLeadPreProduction = (state) => {
  const serialNumber = getIn(['dash', 'serialNumber'], state)
  const isPreProduction = getIn(
    ['truck', 'vehicles', serialNumber, 'isPreProduction'],
    state
  )
  const isLeadSerial = getIn(
    ['truck', 'vehicles', serialNumber, 'isLeadSerial'],
    state
  )
  return isPreProduction && isLeadSerial
}

export const getIsM2Vehicle = (state) => {
  const serialNumber = getIn(['dash', 'serialNumber'], state)
  const vehicleType = getIn(
    ['truck', 'vehicles', serialNumber, 'vehicleType'],
    state
  )
  return vehicleType === VEHICLE_TYPE_M2
}

export const getVehicleDashType = createSelector(vehicleDashType, safeToJS)

// --- Granular sub-selectors for dash state ---
// These return the SAME Immutable ref when their specific subtree is unchanged,
// preventing unnecessary .toJS() calls when unrelated dash state changes (e.g. isDragging).
const dashComponentsImmutable = (state) => state.getIn(['dash', 'components'])
const dashUnassignedComponentsImmutable = (state) =>
  state.getIn(['dash', 'unassignedComponents'])
const dashStagedComponentsImmutable = (state) =>
  state.getIn(['dash', 'stagedComponents'])
const dashVehicleSubType = (state) => state.getIn(['dash', 'vehicleSubType'])
const dashMetaImmutable = (state) => state.getIn(['dash', 'meta'])

// Memoized .toJS() — only recompute when their specific subtree changes
const dashComponentsAsJS = createSelector(dashComponentsImmutable, safeToJS)
const dashUnassignedComponentsAsJS = createSelector(
  dashUnassignedComponentsImmutable,
  safeToJS
)
const dashStagedComponentsAsJS = createSelector(
  dashStagedComponentsImmutable,
  safeToJS
)
const dashMetaAsJS = createSelector(dashMetaImmutable, safeToJS)

const dashAllMetaSelector = createSelector(dashSelector, getOr(Map(), 'meta'))

const dataBookCodesSelector = (state) => {
  const serialNumber = getIn(['dash', 'serialNumber'], state)
  const databookCodes = getIn(
    ['truck', 'vehicles', serialNumber, 'dashboardDbCodes'],
    state
  )
  return databookCodes || List()
}

// Selector for getting databook codes
export const getDataBookCodes = createSelector(dataBookCodesSelector, safeToJS)

export const dashMetaSelector = createSelector(
  dashAllMetaSelector,
  (meta) => meta
)

// Build navigation only for panels with resolved metadata (XR7719 incident).
const panelsSelector = createSelector(dashAllMetaSelector, (meta) => {
  if (!meta.size) return Map()

  const dashPanels = getIn(['dash', 'panels'], meta)
  const overHeadPanels = getIn(['overhead', 'panels'], meta)
  const sleeperPanels = getIn(['sleeper', 'panels'], meta)

  const dashNavPanels = getIn(['dash', 'navPanels'], meta)
  const dashNavOrder = getIn(['dash', 'navOrder'], meta)

  const dashPanelOptions = dashPanels.reduce((acc, curr) => {
    const panelDetails = getIn(['dash', curr], meta)
    if (!panelDetails) return acc
    const { label } = pickToJS(['label'], panelDetails)

    const optionTerm = new Map({
      key: curr,
      value: curr,
      title: label,
    })
    return acc.push(optionTerm)
  }, List())

  const dashNavPanelOptions = dashNavPanels
    ? dashNavPanels.reduce((acc, curr) => {
        const panelDetails = getIn(['dash', curr], meta)
        if (!panelDetails) return acc
        const { label, containedIn } = pickToJS(
          ['label', 'containedIn'],
          panelDetails
        )
        return acc.push(
          new Map({ key: curr, value: curr, title: label, containedIn })
        )
      }, List())
    : List()

  const dashOptions = dashNavOrder
    ? dashNavOrder.reduce((acc, curr) => {
        const panelDetails = getIn(['dash', curr], meta)
        if (!panelDetails) return acc
        const { label, containedIn } = pickToJS(
          ['label', 'containedIn'],
          panelDetails
        )
        return acc.push(
          new Map({ key: curr, value: curr, title: label, containedIn })
        )
      }, List())
    : dashPanelOptions.concat(dashNavPanelOptions)

  const result = Map({ dash: dashOptions })

  const withOverhead = overHeadPanels
    ? result.set(
        'overHeadPanels',
        overHeadPanels.reduce((acc, curr) => {
          const panelDetails = getIn(['overhead', curr], meta)
          if (!panelDetails) return acc
          const { label } = pickToJS(['label'], panelDetails)
          return acc.push(new Map({ key: curr, value: curr, title: label }))
        }, List())
      )
    : result

  const withSleeper = sleeperPanels
    ? withOverhead.set(
        'sleeperPanels',
        sleeperPanels.reduce((acc, curr) => {
          const panelDetails = getIn(['sleeper', curr], meta)
          if (!panelDetails) return acc
          const { label } = pickToJS(['label'], panelDetails)
          return acc.push(new Map({ key: curr, value: curr, title: label }))
        }, List())
      )
    : withOverhead

  return withSleeper
})

export const getDashMeta = createSelector(dashMetaSelector, safeToJS)
export const getDashMetaValues = createSelector(getDashMeta, values)
export const getHasDashMeta = createSelector(dashMetaSelector, notEmpty)
export const getIsLoadingDashConfig = createSelector(dashSelector, (dash) =>
  get('isLoadingDashConfig', dash)
)
export const getDashPanelsOptions = createSelector(panelsSelector, safeToJS)

const dashLocationsSelector = createSelector(dashSelector, (dash) => {
  const locations = getOr(Map(), 'locations', dash)

  return locations
})

// Memoized plain-JS version of dashLocationsSelector.
// Avoids redundant .toJS() calls when multiple selectors recompute against the
// same unchanged locations map (e.g. 30+ Overlay instances on drag-start).
export const dashLocationsAsJSSelector = createSelector(
  dashLocationsSelector,
  (locations) => locations.toJS()
)

const dashLocationsMetaByDetailSelector = createSelector(
  dashAllMetaSelector,
  (meta) => {
    if (!meta.size) return Map()

    const dash = compose(omit(DASH_META_KEYS), getInOr(Map(), ['dash']))(meta)
    const overhead = compose(
      omit(DASH_META_KEYS),
      getInOr(Map(), ['overhead'])
    )(meta)
    const sleeper = compose(
      omit(DASH_META_KEYS),
      getInOr(Map(), ['sleeper'])
    )(meta)

    const allDashMetaByDetail = dash.merge(overhead).merge(sleeper)

    return allDashMetaByDetail
  }
)

export const getDashLocationsMetaByDetail = createSelector(
  dashLocationsMetaByDetailSelector,
  safeToJS
)

const dashPanelsSelector = createSelector(dashMetaSelector, (meta) =>
  meta.reduce((acc, curr) => acc.concat(curr.get('panels')), List())
)

const panelLocationsSelector = createSelector(
  dashPanelsSelector,
  dashLocationsSelector,
  (panels, locations) =>
    panels.reduce((acc, curr) => {
      const panelLocations = compose(
        push(curr),
        getInOr(List(), [curr, 'children'])
      )(locations)

      return acc.set(curr, panelLocations)
    }, Map())
)

export const getPanelLocations = createSelector(
  panelLocationsSelector,
  safeToJS
)

const dashComponentsSelector = createSelector(
  dashSelector,
  getOr(Map(), 'components')
)

const dashStagedComponentsSelector = createSelector(
  dashSelector,
  getOr(Map(), 'stagedComponents')
)

const dashStagedLocationsSelector = createSelector(
  dashSelector,
  getOr(Map(), 'stagedLocations')
)

const dashLocationsWithMetaSelector = createSelector(
  dashLocationsSelector,
  dashLocationsMetaByDetailSelector,
  (locations, meta) => {
    const dashLocations = locations.reduce((acc, currLocation) => {
      const { detail, name } = pickToJS(['detail', 'name'], currLocation)

      // PBI 479416: WP4425's AUDSP layout uses detail DSP; use the location's graphic metadata.
      const detailMeta = get(
        name === DIGITAL_DASH_LOCATION_ID ? name : detail,
        meta
      )

      if (detailMeta) return acc.set(name, currLocation.merge(detailMeta))
      return acc.set(name, currLocation)
    }, Map())

    return dashLocations
  }
)

export const getDashLocations = createSelector(
  dashLocationsWithMetaSelector,
  safeToJS
)
export const getHasDashLocations = createSelector(
  dashLocationsSelector,
  notEmpty
)

export const getDashComponents = createSelector(
  dashComponentsSelector,
  safeToJS
)

const dashComponentSelector = createSelector(
  dashComponentsSelector,
  (_, location) => location,
  (components, location) => getOr(Map(), location, components)
)

const unassignedComponentsSelector = createSelector(
  dashSelector,
  getOr(Map(), 'unassignedComponents')
)

const dashUnassignedComponentSelector = createSelector(
  unassignedComponentsSelector,
  (_, location) => location,
  (components, location) => getOr(Map(), location, components)
)

const stagedComponentsSelector = createSelector(
  dashSelector,
  getOr(Map(), 'stagedComponents')
)

const dashStagedComponentSelector = createSelector(
  stagedComponentsSelector,
  (_, location) => location,
  (components, location) => getOr(Map(), location, components)
)

export const getDashComponent = createSelector(dashComponentSelector, safeToJS)
export const getDashUnassignedComponent = createSelector(
  dashUnassignedComponentSelector,
  safeToJS
)

const componentSelector = createSelector(
  dashComponentSelector,
  dashUnassignedComponentSelector,
  dashStagedComponentSelector,
  (component, unassigned, staged) => {
    if (!component.isEmpty()) return component
    if (!unassigned.isEmpty()) return unassigned
    if (!staged.isEmpty()) return staged
    return Map()
  }
)

export const getComponent = createSelector(componentSelector, safeToJS)

const smartSwitchSelector = createSelector(
  dashSelector,
  getOr(Map(), 'smartSwitches')
)

const searchableComponentsSelector = createSelector(
  dashComponentsSelector,
  unassignedComponentsSelector,
  (assigned, unassigned) => assigned.merge(unassigned)
)

const componentsByPanelNumberSelector = createSelector(
  searchableComponentsSelector,
  (components) =>
    components.reduce(
      (acc, curr) => acc.set(curr.get('panelNumber'), curr),
      Map()
    )
)

const componentsByCapPartNumberSelector = createSelector(
  searchableComponentsSelector,
  (components) =>
    components.reduce((acc, curr) => {
      const capPartNumber = curr.get('capPartNumber')
      if (capPartNumber) return acc.set(capPartNumber, curr)
      return acc
    }, Map())
)

export const getVehicleSubType = createSelector(
  dashSelector,
  get('vehicleSubType')
)

export const getAlliumModelId = createSelector(
  dashSelector,
  get('alliumModelId')
)

export const getIsAllium = createSelector(
  dashSelector,
  getOr(false, 'isAllium')
)

export const getDashIsPreProduction = createSelector(
  dashSelector,
  get('isPreProduction')
)

export const componentsByPanelNumberAndCapPartNumber = createSelector(
  componentsByPanelNumberSelector,
  componentsByCapPartNumberSelector,
  (byPanelNumber, byCapPartNumber, byBaseNumber) =>
    safeToJS(byPanelNumber.merge(byCapPartNumber))
)

const smartSwitchesSelector = createSelector(
  componentsByPanelNumberSelector,
  smartSwitchSelector,
  getVehicleSubType,
  dashLocationsSelector,
  unassignedComponentsSelector,
  (_, location) => location,
  (
    components,
    smartSwitches,
    vehicleSubType,
    locations,
    unassignedComponents,
    location
  ) => {
    const locationsJSON = locations.toJS()
    const catalogResult = smartSwitches.reduce((acc, curr) => {
      const type = curr.get('type')
      const locationPanel = getIn([location, 'panel'], locations)
      const isOverheadLocation = OVERHEAD_PANELS.includes(locationPanel)
      const panelNumber = curr.get('panelNumber')

      if (
        typeof location === 'string' &&
        type === HARDWIRED_SWITCH &&
        vehicleSubType === TRUCK_SUBTYPE_42N &&
        isOverheadLocation
      ) {
        return acc
      }

      if (
        typeof location === 'string' &&
        location.includes(AU_PANEL_LOCATION_PREFIX) &&
        type === HARDWIRED_SWITCH &&
        vehicleSubType !== TRUCK_SUBTYPE_42N
      ) {
        return acc
      }

      if (
        checkIfLocationIsSingleMSFType(
          locationsJSON,
          typeof location === 'string' ? location : location.location
        ) &&
        [HARDWIRED_SWITCH, HARDWIRED_COMPONENT].includes(type) &&
        !panelNumber.startsWith('A12-26539') &&
        [TRUCK_SUBTYPE_38N].includes(vehicleSubType)
      ) {
        return acc
      }

      if (
        checkIfLocationIsSingleMSFType(
          locationsJSON,
          typeof location === 'string' ? location : location.location
        ) &&
        [HARDWIRED_SWITCH, HARDWIRED_COMPONENT].includes(type) &&
        [TRUCK_SUBTYPE_42N].includes(vehicleSubType)
      ) {
        return acc
      }

      // Mark as "already on truck" if it's assigned to a dash location OR in unassigned components
      // This prevents adding duplicates via the +Add dropdown - users should drag from "Parts Without Locations" instead
      const assignedComponents = components
        .filter((c) => !c.get('isSwitchRemoved'))
        .filter(
          (c) => !(c.get('location') || '').startsWith('UNASSIGNED_PANEL_PART')
        )
      const isPlug = curr.get('isPlug')
      const alreadyAssigned = Boolean(assignedComponents.get(panelNumber))
      const inUnassigned = Boolean(
        unassignedComponents
          .filter((c) => !c.get('isSwitchRemoved'))
          .find((c) => c.get('panelNumber') === panelNumber)
      )
      const alreadyOnTruck = alreadyAssigned || inUnassigned

      const part = Map({
        [panelNumber]: curr.set('alreadyOn', alreadyOnTruck && !isPlug),
      })

      return acc.merge(part)
    }, Map())

    // Include unassigned components not already in the smart switches catalog
    // Mark them as alreadyOn so they're disabled in the +Add dropdown (user should drag from "Parts Without Locations")
    const unassignedNotInCatalog = unassignedComponents
      .filter((c) => !c.get('isSwitchRemoved'))
      .reduce((acc, curr) => {
        const panelNumber = curr.get('panelNumber')
        if (panelNumber && !catalogResult.has(panelNumber)) {
          return acc.set(panelNumber, curr.set('alreadyOn', true))
        }
        return acc
      }, Map())

    return catalogResult.merge(unassignedNotInCatalog)
  }
)

export const getSmartSwitches = createSelector(smartSwitchesSelector, safeToJS)

const makeSmartSwitchesSelector = () =>
  createSelector(
    componentsByPanelNumberSelector,
    smartSwitchSelector,
    getVehicleSubType,
    dashLocationsSelector,
    dashLocationsAsJSSelector,
    unassignedComponentsSelector,
    (_, props) => props,
    (
      components,
      smartSwitches,
      vehicleSubType,
      locations,
      locationsJSON,
      unassignedComponents,
      props
    ) => {
      const location = typeof props === 'string' ? props : props?.location
      const catalogResult = smartSwitches.reduce((acc, curr) => {
        const type = curr.get('type')
        const locationPanel = getIn([location, 'panel'], locations)
        const isOverheadLocation = OVERHEAD_PANELS.includes(locationPanel)
        const panelNumber = curr.get('panelNumber')

        if (
          typeof location === 'string' &&
          type === HARDWIRED_SWITCH &&
          vehicleSubType === TRUCK_SUBTYPE_42N &&
          isOverheadLocation
        ) {
          return acc
        }

        if (
          typeof location === 'string' &&
          location.includes(AU_PANEL_LOCATION_PREFIX) &&
          type === HARDWIRED_SWITCH &&
          vehicleSubType !== TRUCK_SUBTYPE_42N
        ) {
          return acc
        }

        if (
          checkIfLocationIsSingleMSFType(
            locationsJSON,
            typeof location === 'string' ? location : location.location
          ) &&
          [HARDWIRED_SWITCH, HARDWIRED_COMPONENT].includes(type) &&
          !panelNumber.startsWith('A12-26539') &&
          [TRUCK_SUBTYPE_38N].includes(vehicleSubType)
        ) {
          return acc
        }

        if (
          checkIfLocationIsSingleMSFType(
            locationsJSON,
            typeof location === 'string' ? location : location.location
          ) &&
          [HARDWIRED_SWITCH, HARDWIRED_COMPONENT].includes(type) &&
          [TRUCK_SUBTYPE_42N].includes(vehicleSubType)
        ) {
          return acc
        }

        const assignedComponents = components
          .filter((c) => !c.get('isSwitchRemoved'))
          .filter(
            (c) =>
              !(c.get('location') || '').startsWith('UNASSIGNED_PANEL_PART')
          )
        const isPlug = curr.get('isPlug')
        const alreadyAssigned = Boolean(assignedComponents.get(panelNumber))
        const inUnassigned = Boolean(
          unassignedComponents
            .filter((c) => !c.get('isSwitchRemoved'))
            .find((c) => c.get('panelNumber') === panelNumber)
        )
        const alreadyOnTruck = alreadyAssigned || inUnassigned

        const part = Map({
          [panelNumber]: curr.set('alreadyOn', alreadyOnTruck && !isPlug),
        })

        return acc.merge(part)
      }, Map())

      const unassignedNotInCatalog = unassignedComponents
        .filter((c) => !c.get('isSwitchRemoved'))
        .reduce((acc, curr) => {
          const panelNumber = curr.get('panelNumber')
          if (panelNumber && !catalogResult.has(panelNumber)) {
            return acc.set(panelNumber, curr.set('alreadyOn', true))
          }
          return acc
        }, Map())

      return safeToJS(catalogResult.merge(unassignedNotInCatalog))
    }
  )

const removedComponentsSelector = createSelector(
  dashSelector,
  getOr(Map(), 'removedComponents')
)

export const getRemovedComponentsList = createSelector(
  removedComponentsSelector,
  (components) => safeToJS(components.valueSeq())
)

const locationsDefinitionSelector = createSelector(
  dashSelector,
  getOr(Map(), 'locations')
)

// Memoized plain-JS version of locationsDefinitionSelector.
// Used where callers need plain-JS access (e.g. checkIfLocationIsSingleMSFType)
// without repeating .toJS() on every recompute.
const locationsDefinitionAsJSSelector = createSelector(
  locationsDefinitionSelector,
  (locations) => locations.toJS()
)

const checkIfLocationIsSingleMSFType = (locations, location) =>
  (locations[location]?.type === LOCATION_TYPE_SINGLE_SWITCH_MSF ||
    locations[location]?.type === LOCATION_TYPE_TBB_SWITCH) &&
  locations[location].singleMsfPresentOnPlusVehicle

export const getAllRemovedSingleMSFComponents = createSelector(
  removedComponentsSelector,
  locationsDefinitionSelector,
  (components, locations) => {
    const locationsInRemovedComponents = components.keySeq()?.toArray() ?? []
    // Performance fix: Convert to JS once outside the filter loop
    const locationsJSON = locations.toJS()
    const filteredRemovedSingleMSFLocations =
      locationsInRemovedComponents?.filter((location) =>
        checkIfLocationIsSingleMSFType(locationsJSON, location)
      ) ?? []

    return Map(
      filteredRemovedSingleMSFLocations.map((loc) => [loc, components.get(loc)])
    )
  }
)

const removedSwitchesSelectors = createSelector(
  dashComponentsSelector,
  filter(get('isSwitchRemoved'))
)

// ToDo: refactor to immutable
export const makeGetPanelPart = () => {
  const getSmartSwitchesForLocation = makeSmartSwitchesSelector()
  return createSelector(
    dashComponentsAsJS,
    dashUnassignedComponentsAsJS,
    dashStagedComponentsAsJS,
    dashVehicleSubType,
    dashMetaAsJS,
    removedSwitchesSelectors,
    getAllRemovedSingleMSFComponents,
    getSmartSwitchesForLocation,
    getDashLocationsMetaByDetail,
    getDashLocations,
    getDataBookCodes,
    getIsPreProduction,
    (__, props) => props,
    (
      components,
      unassignedComponents,
      stagedComponents,
      vehicleSubType,
      dashMeta,
      removedComponents,
      removedSingleMSFComponents,
      smartSwitches,
      locationsMeta,
      locations,
      dataBookCodes,
      isPreProd,
      { location, panelNumber, isPanel }
    ) => {
      const { detail } = propOr({}, location, locations)
      // PBI 479416: AUDSP's detail varies by layout, but its graphic is keyed by location.
      const metaKey = location === DIGITAL_DASH_LOCATION_ID ? location : detail
      const { partOverride, noFallback, swapouts, preferLocationShape } =
        propOr({}, metaKey, locationsMeta)
      const staticOverride = pathOr(
        false,
        [metaKey, 'staticOverride'],
        locationsMeta
      )
      const locationExists = location in locations
      const componentExists = location in components
      // Handle Removed Component
      const removedName = `${REMOVED}_${location}`
      const removedComponent = get(removedName, removedComponents)
      if (removedComponent) {
        return {
          meta: safeToJS(removedComponent),
        }
      }

      // static overrides (what are these?)
      if (partOverride && locationExists && staticOverride) {
        let partNumber = ''
        if (vehicleSubType === TRUCK_SUBTYPE_42N) {
          const dbCode_734 = dataBookCodes.find((code) => code.includes('734'))
          partNumber = pathOr(partOverride, [detail, dbCode_734], locationsMeta)
        } else {
          partNumber = prop(partOverride, locationsMeta)
        }
        const fileName = createPartFileName(partNumber)
        const Part = getPartGraphicByFilename(fileName)

        return {
          Part,
          location,
          meta: {
            ...propOr({}, location, components),
          },
        }
      }

      // Handle Overrides
      // PBI 479416: AUDSP is a digital panel layout, even when no part is assigned to it.
      if (
        partOverride &&
        locationExists &&
        (componentExists || isPanel || location === DIGITAL_DASH_LOCATION_ID)
      ) {
        const override = partOverride

        const fileName = createPartFileName(override)
        const Part = getPartGraphicByFilename(fileName)

        return {
          Part,
          location,
          preferLocationShape,
          meta: {
            ...propOr({}, location, components),
          },
        }
      }

      // Start regular component making
      const partMeta =
        components[location] ||
        smartSwitches[panelNumber] ||
        unassignedComponents[location] ||
        stagedComponents[location]

      // If a component is found at location
      if (partMeta) {
        let pNum = partMeta.panelNumber
        if (swapouts && swapouts[pNum]) pNum = swapouts[pNum]

        // On 42N vehicles, plugs must use the plug graphic even when the part
        // has its own dedicated graphic file (which may be a switch cap shape
        // used on other vehicle types). Check before the happy path. (#485445)
        const displayNumber = get42NPlugDisplayNumber(
          pNum,
          vehicleSubType,
          propOr('', 'panelDescription', partMeta)
        )
        if (displayNumber !== pNum) {
          return {
            Part: getPartGraphicByFilename(createPartFileName(displayNumber)),
            meta: { ...partMeta, panelNumber: displayNumber },
            location,
          }
        }

        const fileNameTxt = createPartFileName(pNum)
        const PartComp = getPartGraphicByFilename(
          fileNameTxt,
          partMeta.imageUrl
        )

        // This is a standard part, the happy path
        if (PartComp && !preferLocationShape) {
          return {
            Part: PartComp,
            meta: partMeta,
            location,
          }
        }

        // Switch has a custom cap.
        if (
          !preferLocationShape &&
          (partMeta.capPartNumber || partMeta.type.includes('switch'))
        ) {
          const Part = getPartGraphicByFilename(
            vehicleSubType === TRUCK_SUBTYPE_38N
              ? `Part__${partMeta.panelNumber?.replaceAll('-', '_')}`
              : `Part__Blank_${vehicleSubType}_Switch`
          )

          if (Part) {
            return {
              Part,
              meta: partMeta,
              location,
              graphicNotFound: vehicleSubType !== TRUCK_SUBTYPE_38N,
            }
          }
        }

        // Is a plug and needs o tbe filled with plug graphic.
        const isPlug = propOr('', 'panelDescription', partMeta)
          .toLowerCase()
          .includes('plug')

        if (isPlug) {
          return {
            Part: getPartGraphicByFilename('06-95304-000'),
            meta: partMeta,
            location,
          }
        }

        // handle possible fallbacks
        const fillForDash = path(['dash', 'fallbacks', detail], dashMeta)
        const fillForOverhead = path(
          ['overhead', 'fallbacks', detail],
          dashMeta
        )
        if (fillForDash || fillForOverhead) {
          // blanks get the unknown part icon on top, other fills do not
          const fillBlankFileName = makeBlankFileName(
            fillForDash || fillForOverhead
          )
          const fillFileName = createPartFileName(
            fillForDash || fillForOverhead
          )
          if (getPartGraphicByFilename(fillFileName)) {
            return {
              Part: getPartGraphicByFilename(fillFileName),
              location,
              graphicNotFound: false,
              meta: partMeta,
            }
          }
          if (getPartGraphicByFilename(fillBlankFileName)) {
            return {
              Part: getPartGraphicByFilename(fillBlankFileName),
              location,
              graphicNotFound: true,
              meta: partMeta,
            }
          }
        }

        return {
          Part: LocationShape,
          meta: partMeta,
          location,
          preferLocationShape,
        }
      }

      // Hanlde if location dictates no fallback used
      if (noFallback) {
        return {
          Part: null,
          location,
          meta: {
            ...propOr({}, location, components),
          },
        }
      }

      // If no component is found for location
      if (!partMeta) {
        // If a component is removed & is a Single MSF
        const removedSingleMSFComponentMeta =
          removedSingleMSFComponents.get(location)
        if (
          (removedSingleMSFComponentMeta ||
            checkIfLocationIsSingleMSFType(locations, location)) &&
          !isPreProd
        ) {
          const PartComp = getPartGraphicByFilename('Part__Fill_Switch')
          if (PartComp) {
            return {
              Part: PartComp,
              meta: removedSingleMSFComponentMeta?.toJS() || {
                ...propOr({}, location, components),
              },
              location,
            }
          }
        }

        // start to check if a fill should be used
        const parentLocationId = pathOr('', [location, 'parent'], locations)
        const parentDetail = pathOr('', [parentLocationId, 'type'], locations)
        const parentComponent = components[parentLocationId]

        // accounts for gaps in overhead panel data
        const locationWillForceFill =
          [OVLHP, OVRHP, OVCPL].includes(parentDetail) ||
          ([DETAIL.B_B].includes(detail) && location.includes('TEM'))

        // fill should be used for MSF, MST, OHSSL locations
        if (parentComponent || locationWillForceFill) {
          const parentLocation = propOr({}, parentLocationId, locations)
          const shouldHaveFill = doesLocationRequireAFill(parentLocation)
          const isLocationInChildren = compose(
            includes(location),
            propOr([], 'children')
          )(parentLocation)

          const shouldShowFill =
            (shouldHaveFill && isLocationInChildren) || locationWillForceFill

          if (shouldShowFill) {
            const fillForDash = path(['dash', 'fallbacks', detail], dashMeta)
            const fillForOverhead = path(
              ['overhead', 'fallbacks', detail],
              dashMeta
            )

            const fillFileName = makeFillFileName(
              fillForDash || fillForOverhead
            )

            if (getPartGraphicByFilename(fillFileName)) {
              return {
                Part: getPartGraphicByFilename(fillFileName),
                location,
                meta: {
                  ...propOr({}, location, components),
                },
              }
            }
          }
        }
      }

      // if everything else fails
      return {
        Part: null,
        meta: {
          ...propOr({}, location, components),
        },
      }
    }
  )
}

const allLogicsSelector = (state) =>
  getInOr(null, [DASH_REDUX_KEY, 'switchLinkedLogic'], state)

const switchLinkedLogicsSelector = createSelector(
  allLogicsSelector,
  (__, props) => props,
  (allLogics, { switchId, truckType }) => {
    const logics = get(switchId, allLogics)

    if (!logics) return List()

    const keys = linkedLogicListKeysByTruckType[truckType]

    return logics.reduce((acc, curr) => {
      // pickToArray as keys will differ for trucktype
      const [uri, description, id] = pickToArray(keys, curr)
      const revision = get('revision', curr)
      return acc.push(
        Map({
          uri,
          description,
          id,
          partNumber: id,
          revision,
        })
      )
    }, List())
  }
)

/**
 * @param {{
 *  serialNumber: string, // doesn't appear to be used
 *  switchId: string,
 *  truckType: string
 * }}
 * @returns {object[]}
 */
export const getSwitchLinkedLogic = createSelector(
  switchLinkedLogicsSelector,
  safeToJS
)

const savedAddedComponentsSelector = createSelector(
  dashSelector,
  getOr(Map(), 'savedAddedComponents')
)

const getIsPartPendingAddedAndRemovedData = createSelector(
  dashComponentsSelector,
  unassignedComponentsSelector,
  removedComponentsSelector,
  savedAddedComponentsSelector,
  (
    dashComponents,
    unassignedComponents,
    removedComponents,
    savedAddedComponents
  ) => {
    if (
      !dashComponents &&
      !unassignedComponents &&
      !removedComponents &&
      !savedAddedComponents
    ) {
      return List()
    }

    // Get assigned components with pendingEdexUpdate flag
    const pendingEdexAssigned = dashComponents
      .valueSeq()
      .filter((component) => component.get('pendingEdexUpdate'))

    // Get unassigned components with pendingEdexUpdate flag (removed switches without location)
    const pendingEdexUnassigned = unassignedComponents
      .valueSeq()
      .filter((component) => component.get('pendingEdexUpdate'))

    // Get components that are being removed (local removals),
    // excluding same-signal swap replacements (Doc 13 - PBI 147363)
    const pendingRemovedComponents = removedComponents
      .valueSeq()
      .filter((component) => !component.get('isSwapReplacement'))

    // Get components that were added and saved (pending programming to DiagnosticLink),
    // excluding same-signal swap replacements (Doc 13 - PBI 147363)
    const pendingSavedAddedComponents = savedAddedComponents
      .valueSeq()
      .filter((component) => !component.get('isSwapReplacement'))

    // Combine all lists - no distinction between added and removed
    // After save, local state is cleared so only API's pendingEdexUpdate items remain
    const allPendingComponents = pendingEdexAssigned
      .concat(pendingEdexUnassigned)
      .concat(pendingRemovedComponents)
      .concat(pendingSavedAddedComponents)

    // Add switchId to each component for rendering
    return allPendingComponents.map((component) =>
      component.set(
        'switchId',
        component.get('location') || component.get('panelNumber')
      )
    )
  }
)

export const selectIsPartPendingList = createSelector(
  getIsPartPendingAddedAndRemovedData,
  safeToJS
)

/**
 * Get the modification count for S&G changes.
 * This counter increments on every add/remove/swap operation.
 * @returns {number}
 */
export const getModificationCount = createSelector(
  dashSelector,
  (dash) => dash.get('modificationCount') || 0
)

/**
 * Get the serial number from the dash Redux state.
 * Used to verify the current Redux state matches the URL vehicle
 * before showing notifications (prevents stale data from previous vehicle).
 */
export const getDashSerialNumber = createSelector(dashSelector, (dash) =>
  dash.get('serialNumber')
)

const draggingItemSelector = createSelector(
  dashSelector,
  getOr(Map(), 'draggingItem')
)

export const getDraggingItem = createSelector(draggingItemSelector, safeToJS)

export const getDraggingItemLocation = createSelector(
  draggingItemSelector,
  get('location')
)

export const makeGetIsBeingDragged = () =>
  createSelector(
    getDraggingItemLocation,
    (_, location) => location,
    (draggingLocation, location) => draggingLocation === location
  )

export const getIsDragging = createSelector(
  dashSelector,
  getOr(false, 'isDragging')
)

export const getDashHasChanges = createSelector(
  dashSelector,
  getOr(false, 'hasChanges')
)

export const getIsSaving = createSelector(
  dashSelector,
  getOr(false, 'isSaving')
)

export const getIsSAndGEditable = createSelector(
  dashSelector,
  getOr(false, 'isEditable')
)

export const getDashVehicleSerialNumber = createSelector(
  dashSelector,
  getOr('', 'serialNumber')
)

export const getDashVehicleType = createSelector(
  dashSelector,
  getOr(null, 'vehicleType')
)

export const getIsSGNotesError = createSelector(
  dashSelector,
  getOr(false, 'isNotesInError')
)

export const getShowPreProductionEditing = createSelector(
  getIsPreProduction,
  getIsSAndGEditable,
  (isPreProduction, isEditable) => isPreProduction && isEditable
)

const dashListsSelector = createSelector(dashAllMetaSelector, (meta) => {
  const allLists = meta
    .reduce((lists, dash) => {
      const type = dash.get('type')
      const list = dash.get('lists').map((l) => l.set('type', type))
      return lists.merge(list)
    }, Map())
    .toList()
    .sort((a, b) => {
      if (a.get('type') === DASH_TYPE_GROUPS.SLEEPER) return 1
      if (b.get('type') === DASH_TYPE_GROUPS.SLEEPER) return -1
      return 0
    })
  return allLists
})

export const getDashLists = createSelector(dashListsSelector, safeToJS)

// ToDo: Update once isPreProduction is in /dashboard GET
const isPreProductionSelector = (state) => {
  const serialNumber = getIn(['dash', 'serialNumber'], state)
  const isPreProduction = getIn(
    ['truck', 'vehicles', serialNumber, 'isPreProduction'],
    state
  )
  return isPreProduction
}

export const getDisplayAllLocations = createSelector(
  dashSelector,
  isPreProductionSelector,
  (dash, isPreProd) => isPreProd && get('displayAllLocations')(dash)
)

const dashListsDefinitionsSelector = createSelector(
  dashListsSelector,
  dashLocationsSelector,
  dashComponentsSelector,
  getDisplayAllLocations,
  isPreProductionSelector,
  (__, props) => props,
  (allLists, locations, components, showAllLocations, isPreProd, { id }) => {
    const sections = id ? allLists.filter(getEq('id', id)) : allLists
    // Performance fix: Convert to JS once at the top, not inside nested loops
    const locationsJSON = locations.toJS()

    const allListsWithLocations = sections.map((section) => {
      const groups = get('groups', section)

      const groupsWithLocations = groups.map((group) => {
        const childrenLookupId = ifElse(
          getEq('isMsfOrMst', true),
          always(get('id', group)),
          always(get('panel', group))
        )(group)

        const locationDetails = getOr(List(), 'detail', group)

        const locationChildren = getInOr(
          List(),
          [childrenLookupId, 'children'],
          locations
        )

        const groupLocations = locationChildren.filter((child) => {
          const location = get(child, locations)
          return locationDetails.contains(get('detail', location))
        })

        // if pre-production and show all locations is selected
        if (isPreProd && showAllLocations)
          return group.set('locations', groupLocations)

        const groupType = getInOr('', [childrenLookupId, 'type'], locations)

        // if group is MSF in OL panel and it is in components, show all locations in MSF
        const isOlPanel =
          childrenLookupId.includes(OVERHEAD_DAY_CAB_LOCATION_ID_PREFIX) ||
          childrenLookupId.includes(OVERHEAD_EXTENDED_CAB_LOCATION_ID_PREFIX)
        const isMsfInOlPanel = groupType === MSF && isOlPanel

        if (isMsfInOlPanel && getOr(false, childrenLookupId, components))
          return group.set('locations', groupLocations)

        // if group is MSF in AU Panel and it is in components, show all locations in MSF
        const inAuPanel = childrenLookupId.includes(AU_PANEL_LOCATION_PREFIX)
        const isMsfInAuPanel = groupType === MSF && inAuPanel

        if (isMsfInAuPanel && getOr(false, childrenLookupId, components))
          return group.set('locations', groupLocations)

        const isMsfMstOhssp =
          groupType === OHSSP || groupType === MSF || groupType === MST

        if (isMsfMstOhssp && !inAuPanel && !isOlPanel)
          return group.set('locations', groupLocations)

        // only show locaitons is componet exists
        const locationsWithComponent = groupLocations.filter(
          (location) =>
            has(location, components) ||
            checkIfLocationIsSingleMSFType(locationsJSON, location)
        )

        return group.set('locations', locationsWithComponent)
      })

      return section.set('groups', groupsWithLocations)
    })

    return allListsWithLocations
  }
)

const dashListsDefinitionsDatabookCodesModifierSelector = createSelector(
  dashListsDefinitionsSelector,
  dataBookCodesSelector,
  dashLocationsSelector,
  (defs, dataBookCodes, locations) => {
    // Some 42N digital-dash vehicles retain the physical AUPNL layout. Only
    // replace its side-panel section when the API supplies an AUDSP layout.
    const hasDigitalAuxPanel = locations.has(DIGITAL_DASH_LOCATION_ID)
    const filteredByDashCode = dataBookCodes.includes(DIGITAL_DASH_CODE)
      ? defs.filter(
          (def) =>
            !hasDigitalAuxPanel || def.get('id') !== AUX_PANEL_LOCATION_ID
        )
      : defs.filter((def) => def.get('id') !== DIGITAL_DASH_LOCATION_ID)

    // Remove sections whose panel type is not present on this vehicle.
    // Sections with no groups (e.g. ICU) are informational and always kept.
    // Sections with groups are only shown when at least one group references a
    // panel location that exists in the vehicle's locationsAsMap.
    return filteredByDashCode.filter((def) => {
      if (def.get('id') === DIGITAL_DASH_LOCATION_ID)
        return locations.has(DIGITAL_DASH_LOCATION_ID)

      // Overhead non-pocket panels (OLP/OEL) always show — their location may
      // be absent from the API even though the physical panel is present.
      // Overhead pocket panels (OCP/ORP/OEC/OER) are passive storage
      // compartments; only show them when the API includes their location.
      if (def.get('type') === DASH_TYPE_GROUPS.OVERHEAD) {
        if (def.get('isOverheadPocket')) return locations.has(def.get('id'))
        return true
      }

      const groups = def.get('groups')
      if (!groups || !groups.size) return true
      return groups.some((group) => {
        const panel = group.get('panel')
        return panel && locations.has(panel)
      })
    })
  }
)

export const getDashListsDefinitions = createSelector(
  dashListsDefinitionsDatabookCodesModifierSelector,
  safeToJS
)

// Memoized filter for unassigned components - filters out removed switches and EDEX-removed parts
// This is a separate selector to maintain proper memoization (filter inside selector body breaks it)
const filteredUnassignedComponentsSelector = createSelector(
  unassignedComponentsSelector,
  (components) =>
    components.filter(
      (c) => !c.get('isSwitchRemoved') && !c.get('removedByEdex')
    )
)

export const getUnassignedComponents = createSelector(
  filteredUnassignedComponentsSelector,
  safeToJS
)

export const getIsUnassignedComponent = createSelector(
  unassignedComponentsSelector,
  (_, { key }) => key,
  (unassignedComponents, key) => has(key, unassignedComponents)
)

const assignedComponentSelector = createSelector(
  dashComponentsSelector,
  filter((component) => isAssignedComponent(component.get('location')))
)

export const getAssignedComponents = createSelector(
  assignedComponentSelector,
  safeToJS
)

export const getLocationsDefinitions = createSelector(
  locationsDefinitionSelector,
  safeToJS
)

const locationDefinitionSelector = createSelector(
  locationsDefinitionSelector,
  (_, location) => location,
  (locations, location) => getOr(Map(), location, locations)
)

export const getLocationDefinition = createSelector(
  locationDefinitionSelector,
  safeToJS
)

/**
 * Get the original locationsAsMap data
 *
 * @return {object}
 */
const originalLocationsAsMapSelector = createSelector(
  dashSelector,
  getIn(['originalDash', 'locationsAsMap'])
)

/**
 * Get the original location definition
 *
 * @param {{ location: string }} location
 * @return {object} location definition as immutable
 */
const originalLocationDefinitionSelector = createSelector(
  originalLocationsAsMapSelector,
  (_, location) => location,
  (locations, location) => getOr(Map(), location, locations)
)

/**
 * Get the original location definition
 *
 * @param {{ location: string }} location
 * @return {object} location definition as POJO
 */
export const getOriginalLocationDefinition = createSelector(
  originalLocationDefinitionSelector,
  safeToJS
)

export const makeGetLocationDefinition = () =>
  createSelector(locationDefinitionSelector, safeToJS)

export const getDashboardType = createSelector(
  dashSelector,
  get('dashboardType')
)

export const getLocationAccepts = createSelector(
  locationsDefinitionSelector,
  locationsDefinitionAsJSSelector,
  dashUnassignedComponentSelector,
  getDashboardType,
  isPreProductionSelector,
  (_, location) => location,
  (
    locations,
    locationsJSON,
    unassignedComponent,
    dashboardType,
    isPreProduction,
    location
  ) => {
    // in preproduction editing unassigned components can be any type, so need errorLocation to know type
    if (isPreProduction && isUnassignedComponent(location)) {
      const errorLocation = get('errorLocation', unassignedComponent)

      return getInOr(UNKNOWN_DETAIL, [errorLocation, 'detail'], locations)
    }

    // in preproduction editing staged components can be any type, so need errorLocation to know type
    if (isPreProduction && isStagedComponent(location)) {
      return getInOr(UNKNOWN_DETAIL, [location, 'detail'], locations)
    }

    // if not preproduction, unassigned component can only be a smart switch
    if (!isPreProduction && isUnassignedComponent(location)) {
      const detail = propOr(null, [dashboardType], SWITCH_DETAIL_BY_DASH_TYPE)
      if (detail) {
        return detail
      }
      return UNKNOWN_DETAIL
    }

    const detail = getInOr(UNKNOWN_DETAIL, [location, 'detail'], locations)
    const type = getIn([location, 'type'], locations)

    if (detail === UNKNOWN_DETAIL) return UNKNOWN_DETAIL

    const msfDetail = propOr(null, [dashboardType], MSF_DETAIL_BY_DASH_TYPE)

    return [
      detail,
      ...insertIf(type === MSF, POC),
      ...insertIf(type === 'POCKET' && msfDetail, msfDetail),
      ...insertIf(
        checkIfLocationIsSingleMSFType(locationsJSON, location),
        ...[
          SWITCH_DETAIL_BY_MODEL_CODE.TBB,
          SWITCH_DETAIL_BY_MODEL_CODE[dashboardType],
        ]
      ),
    ]
  }
)

export const getLocationDetail = createSelector(
  getLocationAccepts,
  (details) => {
    if (isArray(details)) {
      return prop(0, details)
    }
    return details
  }
)

export const makeGetLocationAccepts = () =>
  createSelector(
    locationsDefinitionSelector,
    locationsDefinitionAsJSSelector,
    dashUnassignedComponentSelector,
    getDashboardType,
    isPreProductionSelector,
    (_, location) => location,
    (
      locations,
      locationsJSON,
      unassignedComponent,
      dashboardType,
      isPreProduction,
      location
    ) => {
      // in preproduction editing unassigned components can be any type, so need errorLocation to know type
      if (isPreProduction && isUnassignedComponent(location)) {
        const errorLocation = get('errorLocation', unassignedComponent)

        return getInOr(UNKNOWN_DETAIL, [errorLocation, 'detail'], locations)
      }

      // in preproduction editing staged components can be any type, so need errorLocation to know type
      if (isPreProduction && isStagedComponent(location)) {
        return getInOr(UNKNOWN_DETAIL, [location, 'detail'], locations)
      }

      // if not preproduction, unassigned component can only be a smart switch
      if (!isPreProduction && isUnassignedComponent(location)) {
        const detail = propOr(null, [dashboardType], SWITCH_DETAIL_BY_DASH_TYPE)
        if (detail) {
          return detail
        }
        return UNKNOWN_DETAIL
      }

      const detail = getInOr(UNKNOWN_DETAIL, [location, 'detail'], locations)
      const type = getIn([location, 'type'], locations)

      if (detail === UNKNOWN_DETAIL) return UNKNOWN_DETAIL

      const msfDetail = propOr(null, [dashboardType], MSF_DETAIL_BY_DASH_TYPE)

      return [
        detail,
        ...insertIf(type === MSF, POC),
        ...insertIf(type === 'POCKET' && msfDetail, msfDetail),
        ...insertIf(
          checkIfLocationIsSingleMSFType(locationsJSON, location),
          ...[
            SWITCH_DETAIL_BY_MODEL_CODE.TBB,
            SWITCH_DETAIL_BY_MODEL_CODE[dashboardType],
          ]
        ),
      ]
    }
  )

export const makeGetLocationDetail = () =>
  createSelector(makeGetLocationAccepts(), (details) => {
    if (isArray(details)) {
      return prop(0, details)
    }
    return details
  })

const locationDetailAndMaybeParentDetailSelector = createSelector(
  locationsDefinitionSelector,
  (_, location) => location,
  (locations, location) => {
    const detail = getInOr('', [location, 'detail'], locations)
    const locationOfParent = getIn([location, 'parent'], locations)
    const parentType = getIn([locationOfParent, 'type'], locations)

    if (parentType === MSF) {
      return List([
        detail,
        getInOr('', [locationOfParent, 'detail'], locations),
      ])
    }

    return List([detail])
  }
)

export const getLocationDetailAndMaybeParent = createSelector(
  locationDetailAndMaybeParentDetailSelector,
  safeToJS
)

export const getLocationType = createSelector(
  locationsDefinitionSelector,
  (_, location) => location,
  (locations, location) => getInOr('', [location, 'type'], locations)
)

export const getComponentType = createSelector(
  dashComponentsSelector,
  (_, location) => location,
  (components, location) => getInOr('', [location, 'type'], components)
)

const originalDashSelector = createSelector(
  dashSelector,
  getOr(Map(), 'originalDash')
)

const originalDashComponentsAsMapSelector = createSelector(
  originalDashSelector,
  getOr(Map(), 'componentsAsMap')
)

const originalDashUnassignedAsMapSelector = createSelector(
  originalDashSelector,
  getOr(Map(), 'unassignedComponentsAsMap')
)

const allOriginalComponentsSelector = createSelector(
  originalDashComponentsAsMapSelector,
  originalDashUnassignedAsMapSelector,
  (components, unassigned) => components.merge(unassigned)
)

const allComponentsSelector = createSelector(
  dashComponentsSelector,
  unassignedComponentsSelector,
  stagedComponentsSelector,
  (components, unassigned, staged) => components.merge(unassigned).merge(staged)
)

export const getHasComponentMoved = () =>
  createSelector(
    allOriginalComponentsSelector,
    allComponentsSelector,
    dashLocationsSelector,
    (_, location) => location,
    (allOriginal, allComponents, locations, location) => {
      // Detect MSF-level swaps where panelNumber is unchanged
      if (getIn([location, 'hasBeenSwapped'], allComponents)) return true

      // Detect blank child locations of a newly added MSF
      const parentLocation = getInOr(false, [location, 'parent'], locations)
      if (
        parentLocation &&
        getIn([parentLocation, 'hasBeenAdded'], allComponents)
      )
        return true

      const originalPanelNumber = getIn([location, 'panelNumber'], allOriginal)
      const currentPanelNumber = getIn([location, 'panelNumber'], allComponents)

      if (currentPanelNumber === originalPanelNumber) return false

      return true
    }
  )

export const getDoesLocationParentComponentExist = createSelector(
  dashLocationsSelector,
  dashComponentsSelector,
  (_, location) => location,
  (locations, components, location) => {
    const locationOfParent = getInOr(false, [location, 'parent'], locations)

    if (!locationOfParent) return false

    if (locationOfParent === ICU) return true

    const exists = components.get(locationOfParent)

    return Boolean(exists)
  }
)

const getIsLocationInBlockedList = createSelector(
  blockedListSelector,
  (_, location) => location,
  (blockedList, location) => blockedList.includes(location)
)

const getDoesComponentExistInLocation = createSelector(
  dashComponentsSelector,
  (_, location) => location,
  (components, location) => Boolean(get(location, components))
)

// ToDo: Update once isPreProduction is in /dashboard GET
// const isPreProductionOverheadSelector = createSelector(
//   getIsPreProduction,
//   (_, location) => location,
//   (isPreProduction, location) =>
//     isPreProduction && location.includes(OVERHEAD_LOCATION_PREFIX)
// )

const isComponentMsfPlug = createSelector(
  dashComponentSelector,
  (component) =>
    has('panelDescription', component) &&
    get('panelDescription', component).includes(MSF_CAVITY_PLUG_PART)
)

export const meetsOnDragRequirements = createSelector(
  getDoesLocationParentComponentExist,
  getLocationDetail,
  getIsLocationInBlockedList,
  getDoesComponentExistInLocation,
  isComponentMsfPlug,
  dashLocationsSelector,
  dashUnassignedComponentSelector,
  isPreProductionSelector,
  getIsSAndGEditable,
  (_, location) => location,
  (
    hasParent,
    detail,
    blockedByAnotherPart,
    componentExist,
    isPlug,
    allLocations,
    unassignedComponent,
    isPreProduction,
    isEditable,
    location
  ) => {
    if (!isEditable) return false

    // if (isPreProdOverheadLocation) return false

    if (isStagedComponent(location)) return true

    // if this is an unassigned component
    if (notEmpty(unassignedComponent)) {
      // if is preproduction, need to check if errorLocation is in known locations
      if (isPreProduction) {
        const locationDetail = getOr(
          false,
          'locationDetail',
          unassignedComponent
        )

        const errorLocation = get('errorLocation', unassignedComponent)
        if (errorLocation && get(errorLocation, allLocations)) {
          return true
        }

        return Boolean(locationDetail)
      }
      // if not preproduction, then only a smart switch will appear in this unassigned list
      return true
    }

    if (isPlug) return true

    const isKnownLocation = Boolean(get(location, allLocations))

    return (
      hasParent &&
      Boolean(detail) &&
      !blockedByAnotherPart &&
      componentExist &&
      isKnownLocation
    )
  }
)

export const makeMeetsOnDragRequirements = () =>
  createSelector(
    getDoesLocationParentComponentExist,
    getLocationDetail,
    getIsLocationInBlockedList,
    getDoesComponentExistInLocation,
    isComponentMsfPlug,
    dashLocationsSelector,
    dashUnassignedComponentSelector,
    isPreProductionSelector,
    getIsSAndGEditable,
    (_, location) => location,
    (
      hasParent,
      detail,
      blockedByAnotherPart,
      componentExist,
      isPlug,
      allLocations,
      unassignedComponent,
      isPreProduction,
      isEditable,
      location
    ) => {
      if (!isEditable) return false

      // if (isPreProdOverheadLocation) return false

      if (isStagedComponent(location)) return true

      // if this is an unassigned component
      if (notEmpty(unassignedComponent)) {
        // if is preproduction, need to check if errorLocation is in known locations
        if (isPreProduction) {
          const locationDetail = getOr(
            false,
            'locationDetail',
            unassignedComponent
          )

          const errorLocation = get('errorLocation', unassignedComponent)
          if (errorLocation && get(errorLocation, allLocations)) {
            return true
          }

          return Boolean(locationDetail)
        }
        // if not preproduction, then only a smart switch will appear in this unassigned list
        return true
      }

      if (isPlug) return true

      const isKnownLocation = Boolean(get(location, allLocations))

      return (
        hasParent &&
        Boolean(detail) &&
        !blockedByAnotherPart &&
        componentExist &&
        isKnownLocation
      )
    }
  )

export const getShouldHidePendingPill = () =>
  createSelector(
    getLocationType,
    dashAllMetaSelector,
    (_, location) => location,
    (type, meta, location) => {
      if (location.includes(STAGED_PANEL_PART)) return true
      // no need to account for overhead, can't move whole overhead MSFs things
      return getInOr(false, ['dash', type, 'hidePendingPill'], meta)
    }
  )

export const getCanParentDrop = createSelector(
  draggingItemSelector,
  dashLocationsSelector,
  (_, location) => location,
  (draggingItem, locations, location) => {
    const draggingItemDetail = get('type', draggingItem)
    const locationOfParent = getIn([location, 'parent'], locations)
    const locationParentDetail = getIn([locationOfParent, 'detail'], locations)
    const locationParentType = getIn([locationOfParent, 'type'], locations)

    if (
      locationParentDetail === draggingItemDetail &&
      locationParentType === MSF
    )
      return true

    return false
  }
)

export const makeGetCanParentDrop = () =>
  createSelector(
    draggingItemSelector,
    dashLocationsSelector,
    (_, location) => location,
    (draggingItem, locations, location) => {
      const draggingItemDetail = get('type', draggingItem)
      const locationOfParent = getIn([location, 'parent'], locations)
      const locationParentDetail = getIn(
        [locationOfParent, 'detail'],
        locations
      )
      const locationParentType = getIn([locationOfParent, 'type'], locations)

      if (
        locationParentDetail === draggingItemDetail &&
        locationParentType === MSF
      )
        return true

      return false
    }
  )

const movedComponentsListSelector = createSelector(
  originalDashComponentsAsMapSelector,
  dashComponentsSelector,
  (original, components) => {
    const moved = components.reduce((acc, curr) => {
      if (curr.get('hasBeenAdded')) return acc

      const { location, panelNumber } = pickToJS(
        ['location', 'panelNumber'],
        curr
      )
      const originalPanelNumber = getIn([location, 'panelNumber'], original)

      if (panelNumber !== originalPanelNumber) return acc.push(curr)

      return acc
    }, List())

    return moved
  }
)

export const getMovedComponentsList = createSelector(
  movedComponentsListSelector,
  safeToJS
)

const addedComponentsSelector = createSelector(
  dashComponentsSelector,
  (components) => {
    const whereAdded = components.filter((component) =>
      component.get('hasBeenAdded')
    )

    return whereAdded.valueSeq()
  }
)

export const getAddedComponentsList = createSelector(
  addedComponentsSelector,
  safeToJS
)

export const getDashComponentsList = createSelector(
  dashComponentsSelector,
  (components) => safeToJS(components.valueSeq())
)

export const getDashUnassignedList = createSelector(
  filteredUnassignedComponentsSelector,
  (components) => safeToJS(components.valueSeq())
)

const allGroupsSelector = createSelector(dashListsSelector, (lists) => {
  const groups = lists.map(get('groups'))
  const sections = groups.reduce((acc, curr) => acc.merge(curr), Map())
  return sections
})

export const getGroupLabel = createSelector(
  locationDefinitionSelector,
  allGroupsSelector,
  (locationDef, allGroups) => {
    const parent = get('parent', locationDef)
    const locationName = get('name', locationDef)
    const label = getIn([parent, 'label'], allGroups)

    if (label) return label

    const type = get('type', locationDef)

    if ([MSF, MST].includes(type)) {
      const name = get('name', locationDef)
      return getIn([name, 'panel'], allGroups)
    }

    const detail = get('detail', locationDef)
    if (parent === AGPNL && BREAK_LOCATIONS_42N.includes(locationName)) {
      return strings.lower_panel
    }

    const foundGroup = allGroups.find((group) => {
      const groupDetail = get('detail', group)
      return parent === get('panel', group) && groupDetail.includes(detail)
    })

    return getOr('', 'label', foundGroup)
  }
)

export const getIsLocationInMsf = createSelector(
  dashLocationsSelector,
  (_, location) => location,
  (locations, location) => {
    const locationDef = get(location, locations)
    const parent = get('parent', locationDef)
    const parentType = getIn([parent, 'type'], locations)

    return parentType === MSF || parentType === MST
  }
)

/**
 * Returns true when the location itself is an MSF frame (type === 'MSF').
 * Distinct from getIsLocationInMsf which checks whether the location lives
 * INSIDE an MSF. Used to carve out MSF-frame locations from the CEEA/AG-panel
 * staging-area guard — those frames must remain stageable on 43N vehicles.
 */
const getIsLocationMsfFrame = createSelector(
  dashLocationsSelector,
  (_, location) => location,
  (locations, location) => getIn([location, 'type'], locations) === MSF
)

/**
 * Selector to get the block list without
 * the dragging item's blocked locations.
 * The part is dragged out of it's spot,
 * it can no longer be blocking anything.
 * @returns {Set()}
 */
const canDropBlockedListSelector = createSelector(
  allComponentsSelector,
  blackListSelector,
  draggingItemSelector,
  (components, blackList, draggingItem) => {
    if (draggingItem.isEmpty()) return Set()

    const componentsWithoutDraggingItem = components.delete(
      get('location', draggingItem)
    )

    const blockedLocations = componentsWithoutDraggingItem.reduce(
      (acc, curr) => {
        const location = get('location', curr)
        const blockedList = getInOr(List(), [location, 'blacklist'], blackList)

        return acc.concat(blockedList)
      },
      Set()
    )

    return blockedLocations
  }
)

const getIsLocationOnAnotherPanel = (location, draggingItem, locations) => {
  const locationPanel = getIn([location, 'panel'], locations)
  const draggingItemLocationPanel = getIn(
    [get('location', draggingItem), 'panel'],
    locations
  )
  return locationPanel !== draggingItemLocationPanel
}

export const getIsDashVehicleCHECP = createSelector(
  getDashVehicleType,
  equals(VEHICLE_TYPE_CHEC_PLUS)
)

const getIsLocationEmptyOrMsfPlug = createSelector(
  dashComponentSelector,
  (component) =>
    // Removing Plug part as empty space as per new requirement PBI: 56913
    component.isEmpty()
)

const dashComponentParentSelector = createSelector(
  dashLocationsSelector,
  dashComponentsSelector,
  (_, location) => location,
  (locations, components, location) => {
    const parentLocation = getIn([location, 'parent'], locations)
    return getOr(Map(), parentLocation, components)
  }
)

const getIsLocationInMsfAndMsfExists = createSelector(
  dashComponentParentSelector,
  getIsLocationInMsf,
  (component, isInMsf) => isInMsf && !component.isEmpty()
)

const getIsLocationInTemPanel = createSelector(
  dashLocationsSelector,
  (_, location) => location,
  (locations, location) => {
    const locationDef = get(location, locations)
    const parent = get('parent', locationDef)
    return parent === TEM_PANEL_LOCATION_ID
  }
)

const getIsLocationInSingleSwitchRemovedMSF = createSelector(
  getAllRemovedSingleMSFComponents,
  getDashLocations,
  (_, location) => location,
  (components, locations, location) =>
    components.get(location) ||
    checkIfLocationIsSingleMSFType(locations, location)
)

export const getShouldLocationShowAddBtn = createSelector(
  getIsDashVehicleCHECP,
  getIsLocationEmptyOrMsfPlug,
  getIsLocationInMsfAndMsfExists,
  getIsLocationInSingleSwitchRemovedMSF,
  getIsLocationInTemPanel,
  (_, location) => location,
  (
    isCHECP,
    isEmptyOrPlug,
    isMsfAndExists,
    isRemovedSingleSwitchMSF,
    isInTemPanel,
    location
  ) =>
    isCHECP &&
    isEmptyOrPlug &&
    (isMsfAndExists || isRemovedSingleSwitchMSF || isInTemPanel)
)

const customSwitchGraphicSelector = createSelector(
  dashSelector,
  get('customSwitchGraphics')
)

export const makeGetCustomSwitchGraphic = () =>
  createSelector(
    customSwitchGraphicSelector,
    (_, capPartNumber) => capPartNumber,
    (customSwitchGraphics, capPartNumber) =>
      get(capPartNumber, customSwitchGraphics)
  )

export const makeGetLocationWidthAndHeight = () =>
  createSelector(locationDefinitionSelector, pickToJS(['width', 'height']))

export const makeGetComponentMeta = () =>
  createSelector(
    componentSelector,
    pickToJS(['panelNumber', 'panelDescription', 'capPartNumber', 'isCustom'])
  )

export const makeDisablePartNumber = () =>
  createSelector(getLocationDetail, dashAllMetaSelector, (type, meta) => {
    const config = getInOr(false, ['dash', type, 'noShowPartNumber'], meta)
    if (config) return config

    const overheadConfig = getInOr(
      false,
      ['overhead', type, 'noShowPartNumber'],
      meta
    )
    if (overheadConfig) return overheadConfig

    // this ugly code brought to you by dbCodes
    const sleeper = compose(get(0), immuValues, pick(SLEEPER_DASH_CODES))(meta)

    const sleeperConfig = getInOr(false, [type, 'noShowPartNumber'], sleeper)
    if (sleeperConfig) return sleeperConfig

    return false
  })

const doesLocationRequireAFill = cond([
  [propEq('type', OVCPL), always(true)],
  [propEq('type', OVRHP), always(true)],
  [propEq('type', OVLHP), always(true)],
  [propEq('type', OHSSP), always(true)],
  [propEq('type', MSF), always(true)],
  [propEq('type', MST), always(true)],
  [T, always(false)],
])

export const getIsAddingMsf = createSelector(
  dashSelector,
  getOr(false, 'addMsf')
)

export const getAddingMsfPanel = createSelector(
  dashSelector,
  getOr(false, 'addMsfPanel')
)

export const getIsRemovingMsf = createSelector(
  dashSelector,
  getOr(false, 'removeMsf')
)

export const getRemovingMsfPanel = createSelector(
  dashSelector,
  getOr(null, 'removeMsfPanel')
)

export const getIsMsfModalOpen = createSelector(
  dashSelector,
  getOr(false, 'msfModalOpen')
)

export const getMsfModalPanelId = createSelector(
  dashSelector,
  getOr(null, 'msfModalPanelId')
)

export const getMsfModalEnableAddMsf = createSelector(
  dashSelector,
  getOr(false, 'msfModalEnableAddMsf')
)

export const getMsfModalDisableAddMsfReason = createSelector(
  dashSelector,
  getOr(null, 'msfModalDisableAddMsfReason')
)

export const getCanRemoveMsf = createSelector(
  dashComponentSelector,
  getLocationType,
  (component, type) => {
    if (type !== MSF) return false
    if (component.isEmpty()) return false
    return Boolean(component.get('hasBeenAdded') || !component.get('bom'))
  }
)

export const getIsRemovableEligibleMsf = createSelector(
  getLocationType,
  dashComponentSelector,
  (type, component) => {
    if (type !== MSF) return false
    if (component.isEmpty()) return false
    return Boolean(component.get('hasBeenAdded') || !component.get('bom'))
  }
)

const getCanComponentDropMap = createSelector(
  allComponentsSelector,
  canDropBlockedListSelector,
  draggingItemSelector,
  getIsSAndGEditable,
  getIsAllium,
  dashLocationsAsJSSelector,
  (components, blockedList, draggingItem, isEditable, isAllium, locations) => {
    if (!isEditable) return {}
    if (draggingItem.isEmpty()) {
      return Object.keys(locations).reduce((acc, k) => {
        acc[k] = true
        return acc
      }, {})
    }
    const fromLocation = draggingItem.get('location')
    const allowedKey = isAllium ? 'allowedLocations' : 'allowed'
    const fromAllowed = getIn([fromLocation, allowedKey])(components)
    const result = {}
    for (const toLocation of Object.keys(locations)) {
      if (blockedList.includes(toLocation)) {
        result[toLocation] = false
        continue
      }
      const isDropToLocationAllowed = fromAllowed
        ? includes(toLocation, fromAllowed)
        : false
      if (isDropToLocationAllowed) {
        const swappingComponent = components.get(toLocation)
        if (swappingComponent) {
          const toAllowed = getIn([toLocation, allowedKey])(components)
          result[toLocation] = toAllowed
            ? includes(fromLocation, toAllowed)
            : false
        } else {
          result[toLocation] = true
        }
      } else {
        result[toLocation] = false
      }
    }
    return result
  }
)

export const makeGetCanComponentDrop = () =>
  createSelector(
    getCanComponentDropMap,
    (_, location) => location,
    (dropMap, location) => dropMap[location] ?? false
  )

const getShouldOverlayRenderMap = createSelector(
  draggingItemSelector,
  locationsDefinitionAsJSSelector,
  getCanComponentDropMap,
  getDisplayAllLocations,
  dashComponentsSelector,
  dashLocationsSelector,
  dashLocationsAsJSSelector,
  getIsAddingMsf,
  getIsAllium,
  isPreProductionSelector,
  (
    draggingItem,
    locationDefs,
    canDropMap,
    showAllLocations,
    components,
    locations,
    locationsJSON,
    isAddingMsf,
    isAllium,
    isPreProd
  ) => {
    const draggingItemLocation = draggingItem.get('location')
    const draggingItemDetail = get('type', draggingItem)
    const isDraggingEmpty = draggingItem.isEmpty()
    const isStagedLocation = draggingItemLocation?.includes(STAGED_PANEL_PART)
    const draggingItemType = isDraggingEmpty
      ? undefined
      : locations.getIn([draggingItemLocation, 'type'], undefined)

    const result = {}
    for (const location of Object.keys(locationDefs)) {
      const locationDef = locationDefs[location] || {}
      const { detail, type } = locationDef
      const canDrop = canDropMap[location] ?? false

      if (!detail || !type) {
        result[location] = false
        continue
      }

      if (isAddingMsf && type === MSF) {
        result[location] = true
        continue
      }

      if (
        checkIfLocationIsSingleMSFType(locationsJSON, location) &&
        draggingItemLocation?.startsWith(UNASSIGNED_PANEL_PART) &&
        canDrop
      ) {
        result[location] = true
        continue
      }

      if (isDraggingEmpty) {
        result[location] = false
        continue
      }

      if (draggingItemLocation === location) {
        result[location] = false
        continue
      }

      if (detail === draggingItemDetail && has(location, components)) {
        result[location] = true
        continue
      }

      const doesComponentExistAtLocation = has(location, components)
      const locationType = locations.getIn([location, 'type'], undefined)
      const typesSorted = [draggingItemType, locationType].sort()
      const isOneMsfAndOnePocket =
        typesSorted[0] === MSF && typesSorted[1] === POCKET

      if (doesComponentExistAtLocation) {
        if (isOneMsfAndOnePocket) {
          result[location] = true
          continue
        }
        result[location] = draggingItemDetail === detail
        continue
      }

      if (showAllLocations) {
        if (!isStagedLocation) {
          if (draggingItemType === POCKET && locationType === POCKET) {
            result[location] = false
            continue
          }
          const isLocationSwappableWithPocket = [
            'RSMAN',
            'LSMAN',
            'LPMN1',
            'LPMN2',
          ].includes(location)
          if (
            !isAllium &&
            draggingItemType === MSF &&
            isLocationSwappableWithPocket
          ) {
            result[location] = false
            continue
          }
        }
        result[location] = draggingItemDetail === detail
        continue
      }

      const parentLocation = getIn([location, 'parent'], locations)
      const parentDetail = getInOr('', [parentLocation, 'type'], locations)
      const parentComponent = get(parentLocation, components)

      const locationWillForceFill = [
        OVLHP,
        OVRHP,
        OVCPL,
        MSF,
        MST,
        ...(isPreProd && showAllLocations ? ['PANEL'] : []),
      ].includes(parentDetail)

      if (locationWillForceFill && parentComponent) {
        result[location] = draggingItemDetail === detail
        continue
      }

      result[location] = false
    }
    return result
  }
)

export const makeGetShouldOverlayRender = () =>
  createSelector(
    getShouldOverlayRenderMap,
    (_, location) => location,
    (renderMap, location) => renderMap[location] ?? false
  )

export const getCanComponentDrop = createSelector(
  getCanComponentDropMap,
  (_, location) => location,
  (dropMap, location) => dropMap[location] ?? false
)

export const getShouldOverlayRender = createSelector(
  getShouldOverlayRenderMap,
  (_, location) => location,
  (renderMap, location) => renderMap[location] ?? false
)

export const getDraggingItemType = createSelector(
  draggingItemSelector,
  get('type')
)

export const getIsLocationMsf = createSelector(
  locationDefinitionSelector,
  getEq('type', MSF)
)

export const getIsLocationPocket = createSelector(
  locationDefinitionSelector,
  getEq('type', POCKET)
)

const searchedCustomSwitchPartsSelector = createSelector(
  dashSelector,
  getOr(List(), 'searchedCustomSwitchParts')
)

export const getCustomSwitchParts = createSelector(
  searchedCustomSwitchPartsSelector,
  safeToJS
)

/**
 * Get the immutable List from store.
 * Converts the store Map to List.
 * @return {List}
 */
const selectedCustomGraphicsSelector = createSelector(dashSelector, (state) => {
  const selectedGraphics = getOr(Map(), 'selectedCustomGraphicsToSwitch')(state)
  return selectedGraphics.valueSeq()
})

/**
 * Convert the selectedCustomGraphicsSelector List to plain JS.
 * @return {object[]}
 */
export const getSelectedCustomGraphics = createSelector(
  selectedCustomGraphicsSelector,
  safeToJS
)

/**
 * A selector to find all custom cap graphics that have been removed.
 * @return {List}
 */
const removedCustomGraphicsSelector = createSelector(
  componentsByPanelNumberSelector,
  selectedCustomGraphicsSelector,
  (allComponents, selectedGraphics) => {
    const removed = selectedGraphics.reduce((acc, item) => {
      const component = getOr(Map(), item.get('panelNumber'), allComponents)
      const capPartNumber = getOr('', 'removedCapPartNumber', component)

      if (capPartNumber) {
        return acc.push(
          Map()
            .set('panelNumber', component.get('panelNumber'))
            .set('partNumber', capPartNumber)
            .set('description', component.get('capDescription'))
        )
      }
      return acc
    }, List())

    return removed
  }
)

/**
 * Convert the removedCustomGraphicsSelector List to plain JS.
 * @return {object[]}
 */
export const getRemovedCustomGraphics = createSelector(
  removedCustomGraphicsSelector,
  safeToJS
)

export const getSwitchDetailsChange = createSelector(
  dashSelector,
  getOr(false, 'switchDetailsChanged')
)

export const getCustomGraphicsSearchString = createSelector(
  dashSelector,
  getOr('', 'searchedCustomSwitchPartString')
)

export const getLocationDetails = createSelector(
  dashLocationsSelector,
  (locations) =>
    locations.reduce((acc, currLocation) => {
      const { detail, type } = pickToJS(['detail', 'type'], currLocation)

      if (excludedLocationTypes.includes(type)) {
        return acc
      }

      const isDetailExist = acc.some(
        (locationDetail) => locationDetail.detail === detail
      )

      if (isDetailExist) {
        return acc
      }
      const locationDetail = {
        detail,
        type,
      }
      acc.push(locationDetail)
      return acc
    }, [])
)

export const getUnassignedLocationDetail = createSelector(
  dashSelector,
  (_, location) => location,
  (dash, location) => {
    const type = getInOr(
      false,
      ['unassignedComponents', location, 'locationDetail'],
      dash
    )
    return type
  }
)

export const makeGetUnassignedLocationDetail = () =>
  createSelector(
    dashSelector,
    (_, location) => location,
    (dash, location) => {
      const type = getInOr(
        false,
        ['unassignedComponents', location, 'locationDetail'],
        dash
      )
      return type
    }
  )

export const getAllDashComponents = createSelector(
  allComponentsSelector,
  safeToJS
)

const userRightsSelector = getInOr(List(), ['user', 'userData', 'appRights'])

const locationPanelHasAvailableMsf = createSelector(
  dashLocationsSelector,
  dashComponentsSelector,
  (_, props) => props,
  (locations, components, { definition }) => {
    const { id } = definition
    const children = getInOr(List(), [id, 'children'], locations)

    const msfOrPocketLocations = children.reduce((acc, curr) => {
      const type = locations.getIn([curr, 'type'])

      if ([MSF, POCKET].includes(type)) {
        return acc.push(curr)
      }
      return acc
    }, List())

    const existingMsfOrPocketComponents = msfOrPocketLocations.reduce(
      (acc, curr) => {
        const component = components.get(curr)
        if (component) {
          return acc.push(component)
        }
        const locationChildren = getInOr(List(), [curr, 'children'], locations)
        const hasFactoryInstalledChild = locationChildren.some((child) => {
          const childComponent = components.get(child)
          return childComponent && !childComponent.isEmpty()
        })
        if (hasFactoryInstalledChild) {
          return acc.push(curr)
        }
        return acc
      },
      List()
    )

    return msfOrPocketLocations.size > existingMsfOrPocketComponents.size
  }
)

const locationPanelHasRemovableMsfs = createSelector(
  dashLocationsSelector,
  dashComponentsSelector,
  (_, props) => props,
  (locations, components, { definition }) => {
    const { id } = definition
    const children = getInOr(List(), [id, 'children'], locations)

    return children.some((childLoc) => {
      const type = locations.getIn([childLoc, 'type'])
      if (type !== MSF) return false
      const comp = components.get(childLoc)
      return Boolean(comp && !comp.isEmpty())
    })
  }
)

export const isShowMSFButtonSelector = createSelector(
  dashComponentsSelector,
  blockedListSelector,
  dashLocationsSelector,
  userRightsSelector,
  isPreProductionSelector,
  getIsSAndGEditable,
  getVehicleSubType,
  locationPanelHasAvailableMsf,
  locationPanelHasRemovableMsfs,
  (_, props) => props,
  (
    components,
    blockedList,
    locations,
    userRights,
    isPreProduction,
    isEditable,
    vehicleSubType,
    hasAvailableMsf,
    hasRemovableMsfs,
    { definition }
  ) => {
    const groups = prop('groups', definition)
    const hideAddMsf = prop('hideAddMsf', definition)
    const isOverheadPocket = prop('isOverheadPocket', definition)

    if (
      !isEditable ||
      isPreProduction ||
      !vehicleTypesForAddMsf.includes(vehicleSubType) ||
      !userRights.has(EDIT_PLUS_S_AND_G)
    ) {
      return {
        showMSFButton: false,
        enableMSFButton: false,
        hasRemovableMsfs: false,
      }
    }

    if (isOverheadPocket) {
      return {
        showMSFButton: true,
        enableMSFButton: false,
        hasRemovableMsfs: false,
        disableAddMsfReason: strings.overhead_pocket_cannot_accept_msf,
      }
    }

    if (hideAddMsf) {
      return {
        showMSFButton: false,
        enableMSFButton: false,
        hasRemovableMsfs: false,
      }
    }
    const isEmptyMSF = Object.keys(groups).some((location) => {
      const isBlockedLocation = blockedList.includes(location)

      if (isBlockedLocation && !MSF_POCKET_LOCATIONS_MAP[location]) {
        return false
      }
      const locationDetails = groups[location]

      const locationDetail = locations.get(location)

      if (!locationDetail) return false

      const locationType = locationDetail.get('type')

      const isMsfOrMst = prop('isMsfOrMst', locationDetails)
      if (!isMsfOrMst) return false

      if (locationType === POCKET) return true
      const component = getOr(Map(), location, components)
      if (!component.isEmpty()) return false
      const locationChildren = locations.getIn([location, 'children'])
      if (!locationChildren || locationChildren.size === 0) return true
      // If children have components, it's a factory-installed MSF (not empty)
      return !locationChildren.some((child) => {
        const childComp = components.get(child)
        return childComp && !childComp.isEmpty()
      })
    })

    const canAdd = hasAvailableMsf && isEmptyMSF

    return {
      showMSFButton: true,
      enableMSFButton: true,
      hasRemovableMsfs,
      enableAddMsf: canAdd,
      disableAddMsfReason: !canAdd
        ? strings.no_aftermarket_msf_locations_available
        : undefined,
    }
  }
)

export const getCanStagedComponents = createSelector(
  userRightsSelector,
  (userRights) => userRights.has(EDIT_PLUS_PRE_PROD_S_AND_G)
)

export const getPanelHasRemovableMsfs = createSelector(
  dashLocationsSelector,
  dashComponentsSelector,
  (_, panelId) => panelId,
  (locations, components, panelId) => {
    if (!panelId) return false
    const children = getInOr(List(), [panelId, 'children'], locations)
    return children.some((childLoc) => {
      const type = locations.getIn([childLoc, 'type'])
      if (type !== MSF) return false
      const comp = components.get(childLoc)
      return Boolean(
        comp &&
          !comp.isEmpty() &&
          (comp.get('hasBeenAdded') || !comp.get('bom'))
      )
    })
  }
)

export const getPanelHasAvailableMsfLocations = createSelector(
  dashLocationsSelector,
  dashComponentsSelector,
  blockedListSelector,
  (_, panelId) => panelId,
  (locations, components, blockedList, panelId) => {
    if (!panelId) return false
    const children = getInOr(List(), [panelId, 'children'], locations)
    return children.some((childLoc) => {
      const type = locations.getIn([childLoc, 'type'])
      if (![MSF, POCKET].includes(type)) return false
      const isBlocked = blockedList.includes(childLoc)
      if (isBlocked && !MSF_POCKET_LOCATIONS_MAP[childLoc]) return false

      if (type === POCKET) return true
      const comp = components.get(childLoc)
      return !comp || comp.isEmpty()
    })
  }
)

export const getIsShowMSFButton = createSelector(
  isShowMSFButtonSelector,
  safeToJS
)

export const getCanAddMsf = createSelector(
  dashComponentsSelector,
  dashComponentSelector,
  getLocationType,
  getIsLocationInBlockedList,
  dashLocationsSelector,
  (_, location) => location,
  (components, component, type, blockedByAnotherPart, locations, location) => {
    if (blockedByAnotherPart && !MSF_POCKET_LOCATIONS_MAP[location]) {
      return false
    }

    if (location.includes(UNASSIGNED_PANEL_PART)) {
      return false
    }

    if (type === POCKET) {
      const dependantLocation = MSF_POCKET_LOCATIONS_MAP[location]
      const dependantComponent = getOr(Map(), dependantLocation, components)
      if (dependantComponent.isEmpty()) {
        return true
      }
      return false
    }
    if (component.isEmpty()) {
      const children = getInOr(List(), [location, 'children'], locations)
      if (children.size === 0) return true
      // If children have components, it's a factory-installed MSF (not available)
      return !children.some((child) => {
        const childComp = components.get(child)
        return childComp && !childComp.isEmpty()
      })
    }

    return false
  }
)

export const getIsEligiblePocketOrMsf = createSelector(
  getLocationType,
  (_, location) => location,
  (type, location) => {
    if (type === MSF) {
      return true
    }

    if (type === POCKET) {
      const dependantLocations = Object.keys(MSF_POCKET_LOCATIONS_MAP)
      const hasDependantLocation = dependantLocations.includes(location)

      return !hasDependantLocation
    }

    return false
  }
)

export const getShouldOverrideAcceptDrop = createSelector(
  draggingItemSelector,
  dashLocationsSelector,
  (_, location) => location,
  (draggingItem, locations, location) => {
    if (draggingItem.isEmpty()) {
      return {
        isOverrideType: false,
        overrideType: '',
      }
    }

    const draggingItemType = locations
      .get(draggingItem.get('location'))
      .get('type')

    const dropLocationType = locations.get(location).get('type')

    const isLocationOnAnotherPanel = getIsLocationOnAnotherPanel(
      location,
      draggingItem,
      locations
    )
    const locationPanel = getIn([location, 'panel'], locations)
    if (!isLocationOnAnotherPanel && locationPanel === AGPNL) {
      if (draggingItemType === MSF && dropLocationType === POCKET) {
        return {
          isOverrideType: true,
          overrideType: 'J',
        }
      }

      if (draggingItemType === POCKET && dropLocationType === MSF) {
        return {
          isOverrideType: true,
          overrideType: 'POC',
        }
      }

      return {
        isOverrideType: false,
        overrideType: '',
      }
    }

    return {
      isOverrideType: false,
      overrideType: '',
    }
  }
)

export const getLocationPanel = createSelector(
  dashLocationsSelector,
  (_, location) => location,
  (locations, location) =>
    isUnassignedComponent(location) ? '' : getIn([location, 'panel'], locations)
)

export const getIsLocationOnDash = createSelector(
  dashLocationsSelector,
  (_, location) => location,
  (locations, location) => locations.has(location)
)

export const getStagedComponents = createSelector(
  dashStagedComponentsSelector,
  (stagedComponents) => safeToJS(stagedComponents)
)

export const getStagedLocations = createSelector(
  dashStagedLocationsSelector,
  (stagedLocations) => safeToJS(stagedLocations.valueSeq())
)

export const hasStagedComponents = createSelector(
  getStagedComponents,
  (stagedComponents) => Object.keys(stagedComponents).length > 0
)

export const getIsPanelLocation = createSelector(
  dashLocationsSelector,
  unassignedComponentsSelector,
  (_, location) => location,
  (locations, unassignedComponents, location) => {
    if (has(location, unassignedComponents)) return false

    const panel = getIn([location, 'panel'], locations)

    return panel === location
  }
)

/**
 * A selectors to get the temp storage of edited dash layout location data.
 *
 * @return {DashLayoutLocation} Immutable Object
 */
const layoutLocationToUpdateSelector = createSelector(
  dashSelector,
  get('layoutLocationToUpdate')
)

/**
 * A selectors to get the temp storage of edited dash layout location data.
 *
 * @return {DashLayoutLocation} POJO
 */
export const getLayoutLocationToUpdate = createSelector(
  layoutLocationToUpdateSelector,
  safeToJS
)

/**
 * A selector to get the has approved layout flag.
 *
 * @returns {bool} bool
 */
export const getHasApprovedLayout = createSelector(
  dashSelector,
  get('hasApprovedLayout')
)

/**
 * As the dash rescale project progresses, some dashes are larger than other.
 * To make the larger dashes small, they need to be rescaled.
 * Right hand drive version of these dashes are not rescaled.
 * Check the dashboardType, also known as model code, to see if it needs to be rescaled.
 *
 * @returns {bool} bool
 */
export const getShouldRescaleDash = createSelector(dashSelector, (data) => {
  const dashboardType = data.get('dashboardType')
  const dataBookCodes = data.get('dataBookCodes')
  return (
    ['Q', 'N'].includes(dashboardType) &&
    Boolean(dataBookCodes) &&
    !dataBookCodes.includes(RHD_43N_38N_CODE)
  )
})

/**
 * A selector to manage the async save event flag.
 * @return {boolean}
 */
export const getIsSavingLayoutLocation = createSelector(
  dashSelector,
  get('isSavingLayoutLocation')
)

/**
 * A selector to get any removed by EDEX switches
 * @return {DashComponent[]} immutable
 */
const removedByEdexSwitchesSelector = createSelector(
  dashComponentsSelector,
  compose(immuValues, filter(get('removedByEdex')))
)

/**
 * A selector to convert the immutable removedByEdexSwitchesSelector to POJO
 * @return {DashComponent[]} immutable
 */
export const getRemovedByEdexSwitches = createSelector(
  removedByEdexSwitchesSelector,
  safeToJS
)

/**
 * A selector to get the detail configs like dependency and swapouts by detail
 * The return is the object in the dash definitions META_BY_LOCATION_DETAIL
 *
 * @param {string} detail
 * @return {object} POJO
 */
export const getDependencyOrSwapoutsFromDetail = createSelector(
  dashLocationsMetaByDetailSelector,
  (__, detail) => detail,
  (allMeta, detail) => safeToJS(getOr(Map(), detail, allMeta))
)

/**
 * A selector to know if a location is unique by checking whether the component
 * at that location has any other allowed locations.
 *
 * @param {string} location
 * @return {bool}
 */
export const getIsUniqueLocation = createSelector(
  dashComponentsSelector,
  getIsAllium,
  (_, location) => location,
  (components, isAllium, location) => {
    const component = components.get(location)
    if (!component) return true
    const allowedKey = isAllium ? 'allowedLocations' : 'allowed'
    const allowed = component.get(allowedKey)
    if (!allowed || allowed.size === 0) return true
    const otherAllowed = allowed.filter((loc) => loc !== location)
    return otherAllowed.size === 0
  }
)

/**
 * A selector to get the List of which locations have changed.
 * @returns {strings[List]}
 */
const updatedLocationNamesSelector = createSelector(
  dashSelector,
  getOr(List(), 'updatedLocationNames')
)

/**
 * Convert updatedLocationNamesSelector to safe js
 * @returns {string[]}
 */
export const getUpdatedLocationNames = createSelector(
  updatedLocationNamesSelector,
  safeToJS
)

/**
 * A selector to get updates locations.
 * @return {object[List]}
 */
const updatedLocationsSelector = createSelector(
  dashLocationsSelector,
  updatedLocationNamesSelector,
  (locations, names) => {
    const updatedLocations = names.map((name) => locations.get(name))
    return updatedLocations
  }
)

/**
 * A selector to get the safe js for updatedLocationsSelector
 * @return {object[]}
 */
export const getUpdatedLocations = createSelector(
  updatedLocationsSelector,
  safeToJS
)

/**
 * Find out if location is in sleeper or overhead.
 * @param {string} location
 * @returns boolean
 */
const getIsSleeperOrOverheadLocation = (location) =>
  [
    SLEEPER_LOCATION_PREFIX,
    OVERHEAD_EXTENDED_CAB_LOCATION_ID_PREFIX,
    OVERHEAD_DAY_CAB_LOCATION_ID_PREFIX,
  ].includes(location.slice(0, 2))

/**
 * A selector to know if a componet can move to the staging area.
 * @param {string} location
 * @return {{
 *  canStage: boolean,
 *  reason: string
 * }}
 */
export const showMoveToStagingAreaSelector = createSelector(
  meetsOnDragRequirements,
  getIsPreProduction,
  getIsLocationInMsf,
  getIsUniqueLocation,
  getLocationPanel,
  getDashboardType,
  getIsLocationMsfFrame,
  (_, location) => location,
  (
    canDrag,
    isPreProduction,
    isInMsf,
    isUniqueLocation,
    panel,
    dashType,
    isLocationMsfFrame,
    location
  ) => {
    if (!canDrag) return Map().set('canStage', false).set('reason', '')

    if (!isPreProduction) return Map().set('canStage', false).set('reason', '')

    if (isInMsf) return Map().set('canStage', false).set('reason', '')

    // CEEA vehicles (dashType N/Q/R): AG-panel locations cannot be staged,
    // except when the location itself is an MSF frame (43N cavities must remain stageable).
    if (
      CEEA_DASHBOARD_TYPES.includes(dashType) &&
      panel?.includes('AG') &&
      !isLocationMsfFrame
    )
      return Map().set('canStage', false).set('reason', '')

    if (isUniqueLocation)
      return Map()
        .set('canStage', false)
        .set('reason', strings.not_allowed_to_put_on_staging_message)

    if (isPreProduction && getIsSleeperOrOverheadLocation(location))
      return Map().set('canStage', false).set('reason', '')

    return Map().set('canStage', true).set('reason', '')
  }
)

/**
 * A selector to know if a componet can move to the staging area.
 * @param {string} location
 * @return {{
 *  canStage: boolean,
 *  reason: string
 * }}
 */
export const getShowMoveToStagingArea = createSelector(
  showMoveToStagingAreaSelector,
  safeToJS
)

/**
 * Get all switches that share the same signalName as the current switch.
 * Used for "replace switch with shared signal" functionality (PBI 147363).
 * PBI 147363 - Doc 10: Include current switch (shown as disabled in modal)
 *
 * @param {{
 *  signalName: string,
 *  currentSwitchId: string
 * }}
 * @returns {DashComponent[]} Array of all switches with same signal (including current)
 */
export const getSwitchesBySignalName = createSelector(
  dashComponentsSelector,
  smartSwitchSelector,
  (_, { signalName, currentSwitchId }) => ({ signalName, currentSwitchId }),
  (components, smartSwitches, { signalName, currentSwitchId }) => {
    if (!signalName) return []

    // Get all switches with the same signal from dash (including current for display)
    const allSwitchesWithSignal = components
      .filter(
        (comp) => comp.get('signalName') === signalName && comp.get('switchId')
      )
      .valueSeq()
      .toJS()

    // Deduplicate by panelNumber — prefer originals over hasBeenAdded copies (Doc 12 - PBI 147363)
    const switchMap = allSwitchesWithSignal.reduce((acc, sw) => {
      if (!acc[sw.panelNumber] || !sw.hasBeenAdded) {
        acc[sw.panelNumber] = sw
      }
      return acc
    }, {})

    // PBI 486596: Also include compatible switches from catalog by signalName
    smartSwitches.forEach((sw) => {
      if (
        sw.get('signalName') === signalName &&
        !switchMap[sw.get('panelNumber')]
      ) {
        switchMap[sw.get('panelNumber')] = sw.toJS()
      }
    })

    const uniqueSwitches = Object.values(switchMap)

    // Only return if there are OTHER switches besides current (for modal to show)
    const hasAlternatives = uniqueSwitches.some(
      (sw) => sw.switchId !== currentSwitchId
    )

    return hasAlternatives ? uniqueSwitches : []
  }
)

/**
 * Check if a switch has alternative switches with the same signal.
 * @param {{
 *  signalName: string,
 *  currentSwitchId: string
 * }}
 * @returns {boolean}
 */
export const getHasAlternativeSwitches = createSelector(
  getSwitchesBySignalName,
  (alternatives) => alternatives.length > 0
)

/**
 * Get all signal groups that have alternative switches on the dash.
 * Each group contains the signalName and all unique switches for that signal.
 * Used for multi-tab navigation in ReplaceSwitchModal (PBI 147363).
 * @returns {Array<{ signalName: string, currentPanelNumber: string, currentLocation: string, switches: DashComponent[] }>}
 */
export const getAllSignalGroupsWithAlternatives = createSelector(
  dashComponentsSelector,
  smartSwitchSelector,
  (components, smartSwitches) => {
    const groups = {}

    components.forEach((comp) => {
      const signalName = comp.get('signalName')
      const switchId = comp.get('switchId')
      if (!signalName || !switchId) return

      if (!groups[signalName]) {
        groups[signalName] = []
      }
      groups[signalName].push(comp.toJS())
    })

    return Object.entries(groups).reduce(
      (result, [signalName, dashSwitches]) => {
        // Start with dash components, keyed by panelNumber
        // Prefer originals over hasBeenAdded copies
        const switchMap = dashSwitches.reduce((acc, sw) => {
          if (!acc[sw.panelNumber] || !sw.hasBeenAdded) {
            acc[sw.panelNumber] = sw
          }
          return acc
        }, {})

        // PBI 486510: Merge compatible switches from catalog by signalName
        smartSwitches.forEach((sw) => {
          if (
            sw.get('signalName') === signalName &&
            !switchMap[sw.get('panelNumber')]
          ) {
            switchMap[sw.get('panelNumber')] = sw.toJS()
          }
        })

        const allSwitches = Object.values(switchMap)

        if (allSwitches.length >= 2) {
          const current =
            dashSwitches.find((sw) => !sw.hasBeenAdded) || dashSwitches[0]
          result.push({
            signalName,
            currentPanelNumber: current.panelNumber,
            currentLocation: current.location,
            switches: allSwitches,
          })
        }
        return result
      },
      []
    )
  }
)
